import { Injectable } from '@angular/core';
import { logger } from '@fmnts/common/log';
import {
  createEpik,
  Epik,
  EpikConfig,
  Payment,
  PaymentConfig,
  TransactionInfo,
  TransactionStatus,
} from '@raisenow/epik';
import {
  combineLatest,
  concatMap,
  defer,
  filter,
  first,
  from,
  map,
  Observable,
  of,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  EpikEventError,
  EpikEventName,
  fromEpikEvent,
} from '../rxjs/from-epik-event';
import { pollTransactionInfo } from '../rxjs/poll-transaction-info';
import { epikPaymentValid } from '../rxjs/validate-epik-payment';

/**
 * Describes the behavior for the 'end' of the MPOS tab
 */
export enum MposTabEndBehavior {
  /** Will do nothing */
  None = 'none',
  /** Will redirect from history */
  Back = 'back',
  /** Will close the tab */
  Close = 'close',
}

/**
 * Initializes the EPIK instance.
 *
 * @param epik EPIK instance
 *
 * @returns
 * `Observable` that emits when EPIK is initialzed, then completes.
 */
function init$(epik: Epik) {
  // This kinda fakes an async initialization in order to set up
  // the event listeners first.
  return defer(() => {
    epik.init();
    return of('initialized');
  });
}

/**
 * Represent the status that a payment can be in.
 */
export enum PaymentStatus {
  /** New (clean) payment */
  New = 'new',
  /** Payment is created */
  Created = 'created',
  /** Payment is ready to be sent */
  Ready = 'ready',
  /** Payment is sending -> will be redirected */
  Sending = 'sending',
  /** Payment was sent */
  Sent = 'sent',
  /** Final result is pending */
  Pending = 'pending',
  /** Payment was not successful */
  Failed = 'failed',
  /** Payment was successful */
  Success = 'success',
  /**
   * Payment process was canceled.
   * Should only happen with mPOS.
   */
  Canceled = 'canceled',
}

interface PaymentStore {
  /** Current payment state */
  state: PaymentStatus;
  /** EPIK instance used for payment */
  epik?: Epik;
  /** Payment instance */
  payment?: Payment;
  /** Latest transaction info */
  transactionInfo?: TransactionInfo;
}

/**
 * Actions for the store
 */
enum StoreAction {
  Set,
  Update,
}

/**
 * Interface for the Action 'Set'
 */
interface ISetAction {
  action: StoreAction.Set;
  data: PaymentStore;
}

/**
 * Interface for the Action 'Update'
 */
interface IUpdateAction {
  action: StoreAction.Update;
  data: Partial<PaymentStore>;
}

type IAction = IUpdateAction | ISetAction;

/**
 * Service for making payments via RaiseNow / EPIK.
 */
@Injectable({
  providedIn: 'root',
})
export class RaisenowPaymentService {
  private readonly _log = logger({ name: RaisenowPaymentService.name });
  private readonly initialState: PaymentStore = {
    state: PaymentStatus.New,
  };

  /**
   * Subject for emitting store actions
   */
  private readonly _action$ = new Subject<IAction>();

  /**
   * Store for the payment
   */
  public readonly store$ = this._action$.pipe(
    // This maps our actions to the state in the store
    scan<IAction, PaymentStore>((acc, cur) => {
      switch (cur.action) {
        case StoreAction.Set:
          return { epik: acc.epik, ...cur.data };
        case StoreAction.Update:
          return { ...acc, ...cur.data };
      }
    }, this.initialState),
    startWith(this.initialState),
    // We wanna allow late subscribers to immediately get the state
    shareReplay(1),
  );

  /**
   * `Observable` that emits when a payment is created.
   */
  public payment$ = this.store$.pipe(
    filter(({ state }) => PaymentStatus.Created === state),
    map(({ payment }) => payment),
  );

  /**
   * Creates EPIK instance.
   *
   * @param config Configuration for EPIK instance.
   *
   * @returns
   * EPIK instance.
   */

  public setupEpik(config: EpikConfig) {
    this._update({
      epik: createEpik(config),
    });
  }

  /**
   * Marks the current payment as ready to be sent.
   */
  public markAsReady() {
    this._update({
      state: PaymentStatus.Ready,
    });
  }

  /**
   * Creates payment from the given `paymentConfig`
   *
   * @param paymentConfig payment configuration
   *
   * @returns
   */
  public create(paymentConfig: PaymentConfig) {
    return this.store$.pipe(
      first(),
      map((d) => {
        const payment = d.epik.createPayment(paymentConfig);

        this._update({ state: PaymentStatus.Created, payment });

        return payment;
      }),
    );
  }

  /**
   * Validates the current payment
   *
   * @returns
   * An `Observable` that emits `true`, if the current payment is valid,
   * then completes.
   */
  public validate(): Observable<boolean> {
    return this.store$.pipe(
      first(),
      switchMap(({ payment }) => epikPaymentValid(payment)),
    );
  }

  /**
   * Sends the current payment
   *
   * @returns
   * An `Observable` that completes, when the payment was submitted.
   */
  public send() {
    return this.store$.pipe(
      first(),
      switchMap(({ payment }) => {
        this._update({ state: PaymentStatus.Sending });

        return from(payment.send());
      }),
    );
  }

  /**
   * Will continue an ongoing payment process, after a payment was submitted
   *
   * @returns
   * An `Observable` that emits with the transaction info.
   */
  public continue() {
    this._update({ state: PaymentStatus.Sent });

    return this.store$.pipe(
      first(),
      switchMap(({ epik }) => {
        const fetchPayment$ = fromEpikEvent(
          epik,
          EpikEventName.FETCH_PAYMENT_DATA_END,
        ).pipe(
          map(({ data }) => {
            // When `transactionError` is present, the `transactionInfo`
            // could not be fetched.
            if (data.transactionError) {
              throw new EpikEventError(
                data.transactionError,
                EpikEventName.FETCH_PAYMENT_DATA_END,
                'Transaction error',
              );
            }

            return data.transactionInfo;
          }),
        );

        // This will fetch information for a transaction based
        // on the search params in the current URL and therefore
        // call the event handling defined above.
        return combineLatest([fetchPayment$, init$(epik)]).pipe(
          map(([fetchEvent]) => fetchEvent),
          first(),
        );
      }),
      tap((info) => {
        this.updateTransactionInfo(info);
      }),
    );
  }

  /**
   * @returns
   * An `Observable` that emits with the transaction info and
   * that completes once the status is no longer `pending`.
   */
  public pollTransactionInfo() {
    return this.store$.pipe(
      first(),
      concatMap((data) =>
        pollTransactionInfo(data.epik).pipe(
          map(
            // Combine existing transaction info with
            // polling status
            ({ paymentStatus }): TransactionInfo => ({
              ...data.transactionInfo,
              epayment_status: paymentStatus,
            }),
          ),
        ),
      ),
      tap((info) => {
        this.updateTransactionInfo(info);
      }),
    );
  }

  /**
   * Resets everything for a new payment.
   */
  public reset() {
    this._set({ state: PaymentStatus.New });
  }

  /**
   * Opens a new tab/window with the MPOS terminal web app
   * for the given `transactionId`.
   *
   * @param transactionId Transaction ID
   * @param endBehavior Describes how the MPOS tab should behave when finished
   *
   * @returns
   * Window reference
   */
  public openMposTab(
    transactionId: string,
    endBehavior: MposTabEndBehavior = MposTabEndBehavior.Close,
  ) {
    const forwardUrl = `https://terminal.raisenow.com/pay/${transactionId}?end=${endBehavior}`;
    return window.open(forwardUrl, '_blank');
  }

  /**
   * Updates the language for the current payment process.
   *
   * @param language Language that should be set
   */
  public updateLanguage(language: string) {
    // The store immediately emits on subscription with the latest info
    // this means we can use subscribe in combination with first
    // without needing to unsubscribe
    this.store$.pipe(first()).subscribe(({ epik, payment }) => {
      if (epik) {
        this._updateLanguage(language, epik, payment);
      }
    });
  }

  /**
   * Emits `Set` action to the store
   *
   * @param data Action data
   */
  private _set(data: PaymentStore) {
    this._action$.next({
      action: StoreAction.Set,
      data,
    });
  }

  /**
   * Emits `Update` action to the store.
   *
   * @param data Action data
   */
  private _update(data: Partial<PaymentStore>) {
    this._action$.next({
      action: StoreAction.Update,
      data,
    });
  }

  /**
   * Updates store based on transaction info
   *
   * @param transactionInfo
   */
  private updateTransactionInfo(transactionInfo: TransactionInfo) {
    const updateInfo: Partial<PaymentStore> = {
      transactionInfo,
    };

    switch (transactionInfo?.epayment_status) {
      case TransactionStatus.PENDING:
        updateInfo.state = PaymentStatus.Pending;
        break;
      case TransactionStatus.CANCEL:
        updateInfo.state = PaymentStatus.Canceled;
        break;
      case TransactionStatus.ERROR:
        updateInfo.state = PaymentStatus.Failed;
        break;
      case TransactionStatus.SUCCESS:
        updateInfo.state = PaymentStatus.Success;
        break;
      default:
        this._log.warn(
          'unknown transaction status',
          transactionInfo.epayment_status,
        );
    }

    this._update(updateInfo);
  }

  /**
   * Updates the language on all payment related instances.
   * Requires at least an EPIK instance - otherwise there is
   * nothing to update a language on.
   *
   * @param language The new language
   * @param epik Epik instance
   * @param payment Payment instance
   */
  private _updateLanguage(language: string, epik: Epik, payment?: Payment) {
    // Even though the payment configuration has a `language` property
    // it is not used when initializing the iframe.
    epik.config.language = language;

    if (payment) {
      // Update the information on the payment object
      payment.update({ language });

      // Update translations in iframe
      payment.creditCardIframe?.messenger?.emit(
        'rnw.credit-card.set-language',
        language,
      );
    }
  }
}
